import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import { handleStaleResponse } from "../lib/handle_stale_response";
import { autosaveDraft, cancel as cancelAutosave } from "../components/lib/autosave_draft";

export default class extends Controller {
  static targets = ["wrapper", "trigger", "panel", "option", "submitButton"];
  static values = {
    submitUrl: String,
    draftUrl: String,
    readonly: Boolean,
  };

  connect() {
    this.submitting = false;
    this.openWrapper = null;
    this.boundDocClick = this.#onDocumentClick.bind(this);
    document.addEventListener("click", this.boundDocClick);
    this.#refreshSubmitButton();
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocClick);
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);
  }

  toggle(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    if (trigger.disabled || trigger.dataset.state === "correct" || trigger.dataset.state === "locked") return;
    const wrapper = trigger.closest(".fill-blank-dropdown-wrapper");
    if (!wrapper) return;

    if (this.openWrapper && this.openWrapper !== wrapper) {
      this.#closeWrapper(this.openWrapper);
    }
    if (wrapper.dataset.open === "true") {
      this.#closeWrapper(wrapper);
    } else {
      this.#openWrapper(wrapper);
    }
  }

  triggerKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggle(event);
    } else if (event.key === "Escape" && this.openWrapper) {
      this.#closeWrapper(this.openWrapper);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const trigger = event.currentTarget;
      const wrapper = trigger.closest(".fill-blank-dropdown-wrapper");
      if (wrapper && wrapper.dataset.open !== "true") this.#openWrapper(wrapper);
      const first = wrapper.querySelector(".fill-blank-dropdown__option");
      if (first) first.focus();
    }
  }

  optionKeydown(event) {
    const option = event.currentTarget;
    const wrapper = option.closest(".fill-blank-dropdown-wrapper");
    if (!wrapper) return;
    const options = Array.from(wrapper.querySelectorAll(".fill-blank-dropdown__option"));
    const idx = options.indexOf(option);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      options[(idx + 1) % options.length].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      options[(idx - 1 + options.length) % options.length].focus();
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.select(event);
    } else if (event.key === "Escape") {
      event.preventDefault();
      this.#closeWrapper(wrapper);
      const trigger = wrapper.querySelector(".fill-blank-dropdown");
      trigger?.focus();
    }
  }

  // Pick an option: update the trigger label client-side, autosave the draft.
  select(event) {
    event.preventDefault();
    if (this.submitting) return;
    const option = event.currentTarget;
    const wrapper = option.closest(".fill-blank-dropdown-wrapper");
    this.#closeWrapper(wrapper);
    if (this.readonlyValue) return;

    const trigger = wrapper.querySelector(".fill-blank-dropdown");
    const label = wrapper.querySelector(".fill-blank-dropdown__label");
    const value = option.textContent.trim();
    if (label) label.textContent = value;
    if (trigger) trigger.dataset.selected = value;

    this.#refreshSubmitButton();
    this.#scheduleAutosave();
  }

  submit(event) {
    event?.preventDefault();
    if (this.submitting) return;
    if (this.hasSubmitButtonTarget && this.submitButtonTarget.disabled) return;

    this.submitting = true;
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);

    const token = document.querySelector('meta[name="csrf-token"]')?.content || "";
    const params = new URLSearchParams();
    Object.entries(this.#currentPicks()).forEach(([blankId, answer]) => {
      params.append(`submitted[${blankId}]`, answer);
    });

    fetch(this.submitUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token,
      },
      body: params.toString(),
    })
      .then((r) => {
        if (handleStaleResponse(this, r)) return null;
        return r.text();
      })
      .then((html) => {
        if (html) Turbo.renderStreamMessage(html);
      })
      .finally(() => {
        this.submitting = false;
        // Re-enable the submit button on stale/error responses. A stale
        // response means the exercise is NOT terminal, so a plain refresh
        // (which derives disabled from current picks) is correct.
        this.#refreshSubmitButton();
      });
  }

  #currentPicks() {
    const picks = {};
    this.triggerTargets.forEach((trigger) => {
      const state = trigger.dataset.state;
      if (state === "correct" || state === "locked") return;
      const answer = trigger.dataset.selected || "";
      if (answer.length > 0) picks[trigger.dataset.blankId] = answer;
    });
    return picks;
  }

  #refreshSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;
    const hasPick = Object.keys(this.#currentPicks()).length > 0;
    this.submitButtonTarget.disabled = !hasPick || this.submitting;
  }

  #scheduleAutosave() {
    if (this.submitting || !this.hasDraftUrlValue) return;
    autosaveDraft({
      url: this.draftUrlValue,
      body: { submitted: this.#currentPicks() },
    });
  }

  #onDocumentClick(event) {
    if (!this.openWrapper) return;
    if (!this.openWrapper.contains(event.target)) {
      this.#closeWrapper(this.openWrapper);
    }
  }

  #openWrapper(wrapper) {
    const panel = wrapper.querySelector(".fill-blank-dropdown__panel");
    const trigger = wrapper.querySelector(".fill-blank-dropdown");
    if (!panel || !trigger) return;
    panel.classList.remove("hidden");
    trigger.setAttribute("aria-expanded", "true");
    wrapper.dataset.open = "true";
    this.openWrapper = wrapper;
  }

  #closeWrapper(wrapper) {
    const panel = wrapper.querySelector(".fill-blank-dropdown__panel");
    const trigger = wrapper.querySelector(".fill-blank-dropdown");
    if (!panel || !trigger) return;
    panel.classList.add("hidden");
    trigger.setAttribute("aria-expanded", "false");
    wrapper.dataset.open = "false";
    if (this.openWrapper === wrapper) this.openWrapper = null;
  }
}
